import React, { useState, useRef, useEffect } from "react";
import { MapPin, X } from "lucide-react";
import TajikistanSvg from "./tj.svg";
import Image1 from "../../../assets/map/img1.png";
import { useTranslation } from "react-i18next";
import "../TajikistanMapTours/style.css";
import { API_BASE_URL } from "../../../config/config";

export default function TajikistanMapTours() {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [activeOffice, setActiveOffice] = useState(null);
  const [activeDefault, setActiveDefault] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [svgSize, setSvgSize] = useState({ width: 1000, height: 719 });
  const [originalViewBox, setOriginalViewBox] = useState(null);

  const [offices, setOffices] = useState([]);
  const [default_points, setDefaultPoints] = useState([]);
  const [directions, setDirections] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [, setShowFilters] = useState(false);
  const [, setError] = useState(null);

  const { t, i18n } = useTranslation();

  const svgRef = useRef(null);
  const mapContainerRef = useRef(null);
  const infoCardRef = useRef(null);
  const filterRef = useRef(null);

  const getLangCode = () => {
    const lang = i18n?.language || "ru";
    switch (lang) {
      case "tj":
        return 1;
      case "ru":
        return 2;
      case "en":
        return 3;
      default:
        return 2;
    }
  };

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const lang = getLangCode();
        const res = await fetch(`${API_BASE_URL}/api/regions?lang=${lang}`);

        if (!res.ok) throw new Error(`Regions API error: ${res.status}`);

        const data = await res.json();

        let regionsArray = [];
        if (Array.isArray(data)) {
          regionsArray = data;
        } else if (data.results && Array.isArray(data.results)) {
          regionsArray = data.results;
        } else if (data.data && Array.isArray(data.data)) {
          regionsArray = data.data;
        } else if (data.entities && Array.isArray(data.entities)) {
          regionsArray = data.entities;
        }

        setRegions(regionsArray);
      } catch (err) {
        console.error("[Map] fetchRegions error:", err);
        const fallbackRegions = [
          { id: 1, name: "РРП" },
          { id: 2, name: "Согдийская область" },
          { id: 3, name: "Хатлонская область" },
          { id: 4, name: "Горно-Бадахшанская автономная область" },
          { id: 10, name: "Город Душанбе" },
        ];
        setRegions(fallbackRegions);
      }
    };

    fetchRegions();
  }, [i18n?.language]);

  useEffect(() => {
    const fetchDirections = async () => {
      try {
        const lang = getLangCode();
        const res = await fetch(
          `${API_BASE_URL}/api/directions?lang=${lang}&page=1`
        );

        if (!res.ok) throw new Error(`Directions API error: ${res.status}`);

        const data = await res.json();

        let directionsArray = [];
        if (Array.isArray(data)) {
          directionsArray = data;
        } else if (data.results && Array.isArray(data.results)) {
          directionsArray = data.results;
        } else if (data.data && Array.isArray(data.data)) {
          directionsArray = data.data;
        } else if (data.enttities && Array.isArray(data.enttities)) {
          directionsArray = data.enttities;
        } else if (data.entities && Array.isArray(data.entities)) {
          directionsArray = data.entities;
        }

        setDirections(directionsArray);
      } catch (err) {
        console.error("[Map] fetchDirections error:", err);
        setDirections([]);
      }
    };

    fetchDirections();
  }, [i18n?.language]);

  useEffect(() => {
    let cancelled = false;

    const fetchMapData = async () => {
      setError(null);
      try {
        const lang = getLangCode();

        let url = `${API_BASE_URL}/api/maps/get-projects-map?eventType=6&lang=${lang}`;
        if (selectedDirection) {
          url += `&directionId=${selectedDirection}`;
        }

        const res = await fetch(url);

        if (!res.ok) throw new Error(`Map API error: ${res.status}`);

        const projects = await res.json();

        const officesArr = [];
        const defaultsArr = [];

        for (const project of projects) {
          if (cancelled) break;
          let details = null;
          try {
            const detailsRes = await fetch(
              `${API_BASE_URL}/api/events/${project.projectId}?lang=${lang}`
            );
            if (detailsRes.ok) {
              details = await detailsRes.json();
            } else {
              console.warn(
                `[Map] details fetch failed for projectId=${project.projectId}: ${detailsRes.status}`
              );
            }
          } catch (err) {
            console.warn(
              `[Map] details fetch error for projectId=${project.projectId}:`,
              err
            );
          }

          const officePoint = {
            id: `office_${project.id}`,
            name: project.projectName,
            region: project.regionName,
            address:
              details?.shortDesc ||
              details?.shortDescTj ||
              "No description available",
            phone: details?.title || project.projectName,
            image: details?.image || Image1,
            coordinates: {
              x: project.smallLongitudeOrX ?? 0,
              y: project.smallLatitudeOrY ?? 0,
            },
            projectDetails: details || null,
            directionId: project.directionId,
          };

          const defaultPoint = {
            id: `default_${project.id}`,
            name: project.projectName,
            region: project.regionName,
            address:
              details?.shortDesc ||
              details?.shortDescTj ||
              "No description available",
            phone: details?.title || project.projectName,
            image: details?.image || Image1,
            coordinates: {
              x: project.longitudeOrX ?? 0,
              y: project.latitudeOrY ?? 0,
            },
            projectDetails: details || null,
            directionId: project.directionId,
          };

          officesArr.push(officePoint);
          defaultsArr.push(defaultPoint);
        }

        if (!cancelled) {
          setOffices(officesArr);
          setDefaultPoints(defaultsArr);
        }
      } catch (err) {
        console.error("[Map] fetchMapData error:", err);
        setError(err.message || "Failed to load map data");
      }
    };

    fetchMapData();

    return () => {
      cancelled = true;
    };
  }, [i18n?.language, selectedDirection]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const objectEl = svgRef.current;
      if (objectEl && objectEl.contentDocument) {
        const svg = objectEl.contentDocument.querySelector("svg");
        if (svg) {
          const rect = svg.getBoundingClientRect();
          setSvgSize({
            width: rect.width,
            height: rect.height,
          });
        }
      }
    };

    const handleLoad = () => {
      setTimeout(() => {
        handleResize();
        window.addEventListener("resize", handleResize);
      }, 0);
    };

    const objectEl = svgRef.current;
    if (objectEl) {
      objectEl.addEventListener("load", handleLoad);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (objectEl) {
        objectEl.removeEventListener("load", handleLoad);
      }
    };
  }, []);

  useEffect(() => {
    const handleLoad = () => {
      if (svgRef.current) {
        const svgDoc = svgRef.current.contentDocument;
        if (!svgDoc) return;
        const svgElement = svgDoc.querySelector("svg");
        if (!svgElement) return;

        const originalVB = svgElement.getAttribute("viewBox");
        if (originalVB) {
          setOriginalViewBox(originalVB);
        }

        const regions = svgDoc.querySelectorAll("path");

        regions.forEach((region) => {
          region.addEventListener("click", (e) => {
            e.stopPropagation();
            const regionId = region.getAttribute("id");
            if (regionId === selectedRegion && isZoomed) {
              resetView(svgElement, regions);
            } else if (regionId) {
              zoomToRegion(regionId, region, svgElement, regions);
            }
          });

          region.addEventListener("mouseenter", () => {
            if (!isZoomed) region.style.fill = "#e0e0ff";
          });

          region.addEventListener("mouseleave", () => {
            if (!isZoomed) region.style.fill = "";
          });
        });

        svgElement.addEventListener("click", (e) => {
          const btn = document.getElementById("resetButton");
          if (btn) btn.click();
        });
      }
    };

    const zoomToRegion = (regionId, regionElement, svgElement, allRegions) => {
      const bbox = regionElement.getBBox();
      const padding = bbox.width * 0.1;
      const newViewBox = `${bbox.x - padding} ${bbox.y - padding} ${
        bbox.width + padding * 2
      } ${bbox.height + padding * 2}`;

      svgElement.style.transition = "all 1.6s cubic-bezier(0.23, 1, 0.32, 1)";
      svgElement.setAttribute("viewBox", newViewBox);

      allRegions.forEach((r) => {
        if (r.getAttribute("id") !== regionId) {
          r.style.opacity = "0.2";
          r.style.filter = "blur(4px)";
          r.style.transition = "all 0.6s ease-out";
        } else {
          r.style.opacity = "1";
          r.style.filter = "none";
          r.style.fill = "#d0d0ff";
        }
      });

      setSelectedRegion(regionId);
      setIsZoomed(true);
      setActiveOffice(null);
      setActiveDefault(null);
    };

    const resetView = (svgElement, allRegions) => {
      svgElement.style.transition = "all 1.2s ease-in-out";
      if (originalViewBox) {
        svgElement.setAttribute("viewBox", originalViewBox);
      }
      allRegions.forEach((r) => {
        r.style.opacity = "1";
        r.style.filter = "none";
        r.style.fill = "";
        r.style.transition = "all 0.6s ease-in-out";
      });
      setSelectedRegion(null);
      setIsZoomed(false);
      setActiveOffice(null);
      setActiveDefault(null);
    };

    const svgElement = svgRef.current;
    if (svgElement) {
      svgElement.addEventListener("load", handleLoad);
    }

    return () => {
      if (svgElement) {
        svgElement.removeEventListener("load", handleLoad);
      }
    };
  }, [originalViewBox, selectedRegion, isZoomed]);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (
        activeOffice &&
        infoCardRef.current &&
        !infoCardRef.current.contains(e.target) &&
        !e.target.closest(".office-marker")
      ) {
        setActiveOffice(null);
      }
      if (
        activeDefault &&
        infoCardRef.current &&
        !infoCardRef.current.contains(e.target) &&
        !e.target.closest(".office-marker")
      ) {
        setActiveDefault(null);
      }
    };

    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [activeOffice, activeDefault]);

  const handleOfficeClick = (office, e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveOffice(
      activeOffice && activeOffice.id === office.id ? null : office
    );
  };

  const handleDefaultClick = (office, e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDefault(
      activeDefault && activeDefault.id === office.id ? null : office
    );
  };

  const handleDirectionFilter = (directionId) => {
    setSelectedDirection(
      directionId === selectedDirection ? null : directionId
    );
    setShowFilters(false);
    setActiveOffice(null);
    setActiveDefault(null);
  };

  const clearFilter = () => {
    setSelectedDirection(null);
    setActiveOffice(null);
    setActiveDefault(null);
  };

  let selectedRegionName = null;
  if (selectedRegion) {
    const svgToApiMapping = {
      TJDU: 10,
      TJGB: 4,
      TJRA: 1,
      TJSU: 2,
      TJKT: 3,
    };

    const apiId = svgToApiMapping[selectedRegion];

    const fallbackRegionNames = {
      1: "РРП",
      2: "Согдийская область",
      3: "Хатлонская область",
      4: "Горно-Бадахшанская автономная область",
      10: "Город Душанбе",
    };

    let regionData = null;
    if (apiId) {
      regionData =
        regions.find((region) => {
          const id =
            region.code || region.id || region.regionCode || region.regionId;
          return Number(id) === Number(apiId);
        }) ||
        regions.find((region) =>
          region.name
            ?.toLowerCase()
            .includes(fallbackRegionNames[apiId].toLowerCase())
        );
    }

    selectedRegionName =
      regionData?.name ||
      regionData?.title ||
      regionData?.regionName ||
      fallbackRegionNames[apiId] ||
      selectedRegion;
  }

  const filteredOffices = selectedRegionName
    ? offices.filter((office) => {
        const officeRegion = office.region
          ?.toLowerCase()
          .trim()
          .replace(/\s+/g, " ");
        const selectedRegion = selectedRegionName
          ?.toLowerCase()
          .trim()
          .replace(/\s+/g, " ");
        return officeRegion === selectedRegion;
      })
    : offices;

  const getSelectedDirectionName = () => {
    if (!selectedDirection) return null;
    const direction = directions.find((d) => d.id === selectedDirection);
    return direction ? direction.name || direction.title : null;
  };

  const placeLabel = (x, y) => {
    return {
      left: `${(x / 1100) * 100}%`,
      top: `${(y / 919) * 100}%`,
      transform: "translate(-50%, -50%)",
      position: "absolute",
    };
  };

  return (
    <div className="w-full mx-auto text-white sm:px-4 border-0 shadow-sm rounded-3 ">
      <div
        className="flex flex-col w-full max-w-6xl  rounded p-4 text-black bg-white px-2 sm:px-4"
        style={{ border: 0 }}
      >
        {/* <h2 className="flex items-center text-center text-2xl sm:text-3xl md:text-4xl font-monster font-bold mb-4 sm:mb-6 md:mb-8 text-black">
          <b>{t("MapTitle")}</b>
        </h2> */}

        {/* <div className="filter-wrapper" ref={filterRef}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="filter-button"
          >
            <SlidersHorizontal size={18} />
            <span className="whitespace-nowrap">
              {selectedDirection
                ? getSelectedDirectionName()
                : t("filter") || "Filter by Direction"}
            </span>
            {selectedDirection && (
              <X
                size={16}
                className="ml-1 hover:text-red-500 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  clearFilter();
                }}
              />
            )}
          </button>

          {showFilters && (
            <div className="filter-dropdown">
              <button
                onClick={() => handleDirectionFilter(null)}
                className="filter-item"
              >
                {t("All") || "All Directions"}
              </button>
              {directions.map((direction) => (
                <button
                  key={direction.id}
                  onClick={() => handleDirectionFilter(direction.id)}
                  className="filter-item"
                >
                  {direction.name || direction.title}
                </button>
              ))}
            </div>
          )}
        </div> */}

        <div
          ref={mapContainerRef}
          className="w-full rounded-lg bg-gray-50 relative"
          style={{ backgroundColor: "rgba(231, 15, 15, 0)" }}
        >
          <div className="w-full" style={{ position: "relative" }}>
            <div className="relative w-full aspect-[1100/919]">
              <div className="w-full h-full flex justify-center items-center ">
                <object
                  ref={svgRef}
                  data={TajikistanSvg}
                  type="image/svg+xml"
                  className="w-full"
                  style={{
                    maxWidth: "1000px",
                    width: "100%",
                    height: window.innerWidth < 768 ? "300px" : "600px",
                    maxHeight: window.innerWidth < 768 ? "50vh" : "80vh",
                    margin: "0 auto",
                    display: "block",
                  }}
                  aria-label="Interactive map of Tajikistan"
                >
                  Map of Tajikistan
                </object>
              </div>
            </div>
            {!isZoomed && (
              <>
                <div
                  style={{
                    ...placeLabel(680, 600),
                    color: "#6F9C76",
                    fontWeight: "bold",
                    userSelect: "none",
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                  }}
                >
                  {t("GBAO")}
                </div>

                <div
                  style={{
                    ...placeLabel(410, 200),
                    color: "#6F9C76",
                    fontWeight: "bold",
                    userSelect: "none",
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                  }}
                >
                  {t("Sogd")}
                </div>

                <div
                  style={{
                    ...placeLabel(350, 620),
                    color: "#6F9C76",
                    fontWeight: "bold",
                    userSelect: "none",
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                  }}
                >
                  {t("Khatlon")}
                </div>

                <div
                  style={{
                    ...placeLabel(440, 450),
                    color: "#6F9C76",
                    fontWeight: "bold",
                    userSelect: "none",
                    fontSize: window.innerWidth < 768 ? "10px" : "12px",
                  }}
                >
                  {t("RRP")}
                </div>
              </>
            )}
            {isZoomed && (
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none border-4 border-purple-500">
                {filteredOffices.map((office) => {
                  return (
                    <div
                      style={{
                        position: "absolute",
                        left: `${office.coordinates.x}%`,
                        top: `${office.coordinates.y}%`,
                      }}
                      onClick={(e) => handleOfficeClick(office, e)}
                    >
                      <MapPin
                        size={window.innerWidth < 768 ? 20 : 28}
                        color="red"
                        fill="white"
                        className="animate-pulse"
                        style={{
                          filter: "drop-shadow(0px 3px 3px rgba(0,0,0,0.3))",
                          animation: "pulse 1.5s infinite",
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {!isZoomed && (
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                {default_points.map((office) => (
                  <div
                    key={`marker-${office.id}`}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer pointer-events-auto office-marker"
                    style={{
                      left: `${office.coordinates.x}%`,
                      top: `${office.coordinates.y}%`,
                      position: "absolute",
                      animation: "fadeInBounce 0.6s ease-out",
                    }}
                    onClick={(e) => handleDefaultClick(office, e)}
                  >
                    <MapPin
                      size={window.innerWidth < 768 ? 20 : 25}
                      color="red"
                      fill="white"
                      className="animate-pulse"
                      style={{
                        filter: "drop-shadow(0px 3px 3px rgba(0,0,0,0.3))",
                        animation: "pulse 1.5s infinite",
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            {activeDefault && (
              <div
                ref={infoCardRef}
                className="map-popup-card"
                style={{
                  width: window.innerWidth < 768 ? "280px" : "320px",
                  left:
                    window.innerWidth < 768
                      ? "5%"
                      : `${
                          svgSize.width > 440
                            ? activeDefault.coordinates.x > 70
                              ? (((svgSize.width *
                                  activeDefault.coordinates.x) /
                                  100 -
                                  320) *
                                  100) /
                                svgSize.width
                              : activeDefault.coordinates.x
                            : 10
                        }%`,
                  top:
                    window.innerWidth < 768
                      ? "10%"
                      : `${
                          svgSize.width > 440
                            ? activeDefault.coordinates.y + 1
                            : 0
                        }%`,
                }}
              >
                <div
                  className="map-popup-close"
                  onClick={() => setActiveDefault(null)}
                >
                  <X size={16} />
                </div>

                <div className="map-popup-content">
                  {activeDefault.image && (
                    <div className="map-popup-image-container">
                      <img
                        src={activeDefault.image}
                        alt={activeDefault.name}
                        className="map-popup-image"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}

                  <div className="map-popup-info">
                    <h3
                      className="map-popup-title"
                      dangerouslySetInnerHTML={{ __html: activeDefault.name }}
                    ></h3>
                    <div className="map-popup-details">
                      <div className="map-popup-detail-item">
                        <span className="map-popup-label">{t("region")}</span>
                        <span className="map-popup-value">
                          {activeDefault.region}
                        </span>
                      </div>
                      <div className="map-popup-detail-item">
                        <span className="map-popup-label">
                          {t("description")}
                        </span>
                        <span
                          className="map-popup-value"
                          dangerouslySetInnerHTML={{
                            __html: activeDefault.address,
                          }}
                        ></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {activeOffice && (
              <div
                ref={infoCardRef}
                className="map-popup-card"
                style={{
                  width: window.innerWidth < 768 ? "280px" : "320px",
                  left:
                    window.innerWidth < 768
                      ? "5%"
                      : `${
                          svgSize.width > 440
                            ? activeOffice.coordinates.x > 70
                              ? (((svgSize.width * activeOffice.coordinates.x) /
                                  100 -
                                  320) *
                                  100) /
                                svgSize.width
                              : activeOffice.coordinates.x
                            : 10
                        }%`,
                  top:
                    window.innerWidth < 768
                      ? "10%"
                      : `${
                          svgSize.width > 440
                            ? activeOffice.coordinates.y + 1
                            : 0
                        }%`,
                }}
              >
                <div
                  className="map-popup-close"
                  onClick={() => setActiveOffice(null)}
                >
                  <X size={16} />
                </div>

                <div className="map-popup-content">
                  {activeOffice.image && (
                    <div className="map-popup-image-container">
                      <img
                        src={activeOffice.image}
                        alt={activeOffice.name}
                        className="map-popup-image"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}

                  <div className="map-popup-info">
                    <h3
                      className="map-popup-title"
                      dangerouslySetInnerHTML={{ __html: activeOffice.name }}
                    ></h3>
                    <div className="map-popup-details">
                      <div className="map-popup-detail-item">
                        <span className="map-popup-label">Region:</span>
                        <span className="map-popup-value">
                          {activeOffice.region}
                        </span>
                      </div>
                      <div className="map-popup-detail-item">
                        <span className="map-popup-label">Description:</span>
                        <span
                          className="map-popup-value"
                          dangerouslySetInnerHTML={{
                            __html: activeOffice.address,
                          }}
                        ></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mt-4">
          {isZoomed && (
            <button
              style={{ backgroundColor: "#6D9273" }}
              className="px-4 py-2 bg-blue-600 text-black border rounded hover:bg-blue-700 transition text-white"
              id="resetButton"
              onClick={() => {
                if (svgRef.current?.contentDocument) {
                  const svgElement =
                    svgRef.current.contentDocument.querySelector("svg");
                  const regions =
                    svgRef.current.contentDocument.querySelectorAll("path");
                  if (svgElement && regions && originalViewBox) {
                    svgElement.setAttribute("viewBox", originalViewBox);
                    regions.forEach((r) => {
                      r.style.opacity = "1";
                      r.style.filter = "none";
                      r.style.fill = "";
                      r.style.transition = "all 0.6s ease-in-out";
                    });
                    setSelectedRegion(null);
                    setIsZoomed(false);
                    setActiveOffice(null);
                    setActiveDefault(null);
                  }
                }
              }}
            >
              {t("WholeMap")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
